import {toLower} from "lodash"

export type FileMetadata = {
	name: string
	size: number
}

export enum FileType {
	MP3 = "MP3",
	MP4 = "MP4",
	JPEG = "JPEG",
	JPG = "JPG",
	PNG = "PNG",
	WAV = "WAV",
	PDF = "PDF",
	DOCX = "DOCX",
}

export const convertFileExtensionToFileType = (extension?: string): FileType | undefined => {
	switch (toLower(extension)) {
		case "mp3":
			return FileType.MP3
		case "mp4":
			return FileType.MP4
		case "jpeg":
			return FileType.JPEG
		case "jpg":
			return FileType.JPG
		case "png":
			return FileType.PNG
		case "wav":
			return FileType.WAV
		case "pdf":
			return FileType.PDF
		case "docx":
			return FileType.DOCX
		default:
			return undefined
	}
}

export function fileReader(file: File) {
	return new Promise((resolve) => {
		const reader = new FileReader()
		reader.onload = () => {
			resolve(reader.result)
		}

		reader.readAsBinaryString(file)
	})
}

export function openLinkNewTab(link: string) {
	const a = document.createElement("a")
	document.body.appendChild(a)
	a.target = "_blank"
	a.style.display = "none"
	a.href = link
	a.click()
	URL.revokeObjectURL(a.href)
	a.remove()
}

export function downloadFile(filename: string, data: any, fileType: string) {
	const fileData = new Blob([data], {type: fileType})
	const a = document.createElement("a")
	document.body.appendChild(a)
	a.style.display = "none"
	a.href = URL.createObjectURL(fileData)
	a.download = filename
	a.click()
	URL.revokeObjectURL(a.href)
	a.remove()
}

export const formatFileSize = (sizeInBytes: number): string => {
	const kilobyte = 1000
	const megabyte = kilobyte * 1000
	const gigabyte = megabyte * 1000

	if (sizeInBytes < kilobyte) {
		return `${sizeInBytes} B`
	} else if (sizeInBytes < megabyte) {
		return `${(sizeInBytes / kilobyte).toFixed(2)} KB`
	} else if (sizeInBytes < gigabyte) {
		return `${(sizeInBytes / megabyte).toFixed(2)} MB`
	} else {
		return `${(sizeInBytes / gigabyte).toFixed(2)} GB`
	}
}

export const extractExtensionAndFileName = (name?: string): {extension?: string; nameWithoutExtension: string} => {
	if (!name) {
		return {nameWithoutExtension: ""}
	}
	const lastDotIndex = name.lastIndexOf(".")
	if (lastDotIndex !== -1) {
		const extension = name.slice(lastDotIndex + 1)
		const nameWithoutExtension = name.slice(0, lastDotIndex)
		return {extension, nameWithoutExtension}
	} else {
		return {nameWithoutExtension: name}
	}
}

export const getMimeTypeByFileName = (fileName?: string): string | undefined => {
	const fileExtension = extractExtensionAndFileName(fileName).extension
	switch (toLower(fileExtension)) {
		case "jpeg":
		case "jpg":
			return "image/jpeg"
		case "png":
			return "image/png"
		case "pdf":
			return "application/pdf"
		case "docx":
			return "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
		case "mp3":
			return "audio/mpeg"
		case "mp4":
			return "video/mp4"
		case "wav":
			return "audio/wav"
		default:
			return undefined
	}
}

export const base64ToBlob = (base64File: string): {blob: Blob; contentType: string} => {
	// Extract the base64 content without the data URL prefix if it exists
	const base64Data = base64File.includes("base64,") ? base64File.split("base64,")[1] : base64File

	// Convert base64 to binary
	const binaryString = atob(base64Data)
	const bytes = new Uint8Array(binaryString.length)
	for (let i = 0; i < binaryString.length; i++) {
		bytes[i] = binaryString.charCodeAt(i)
	}

	// Create a Blob from the binary data
	const blob = new Blob([bytes])

	// Detect content type from base64 string
	let contentType = "application/octet-stream"
	if (base64File.includes("data:")) {
		contentType = base64File.split(";")[0].split(":")[1]
	}

	return {blob, contentType}
}

export const readFileAsBase64 = (file: File): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = () => {
			resolve(reader.result as string)
		}
		reader.onerror = (error) => {
			reject(error)
		}
		reader.readAsDataURL(file)
	})
}
